import {
  type EKycVerificationStatus,
  GetExamEventByIdDocument,
  GetExamEventExamineeCountSummaryDocument,
  SearchExamEventExamineesDocument,
  useGetExamineeSpreadsheetUploadSignedUrlLazyQuery,
  useImportExamineesSpreadsheetMutation,
} from '@exam-center/graphql';
import { Dialog, DialogTitle, Divider } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

import { ImportExamineesViaFileForm, type ImportExamineesViaFileFormInput } from '../../components';

interface ImportExamineesViaFileModalProps {
  onClose: () => void;
  open: boolean;
  examEventId: string;
  importTemplateUrl: string;
}

export interface ErrorDetail {
  code: string;
  property?: string;
  details?: {
    targetRows?: number[];
    kycStatus?: EKycVerificationStatus.Approved | EKycVerificationStatus.PendingApproval | string;
    [key: string]: unknown;
  };
}

export default function ImportExamineesViaFileModal(
  props: Readonly<ImportExamineesViaFileModalProps>,
) {
  const { onClose, open: isOpen, examEventId, importTemplateUrl } = props;
  const [errorMessages, setErrorMessages] = useState<Map<string, ErrorDetail[]> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showPromiseSnackbar } = useSnackbar();

  const [getUploadSignedUrl] = useGetExamineeSpreadsheetUploadSignedUrlLazyQuery({
    fetchPolicy: 'network-only',
    onError: (error) => {
      setErrorMessages(new Map([[error.message, []]]));
    },
  });
  const [importExamineesSpreadsheet] = useImportExamineesSpreadsheetMutation({
    onError: (importError) => {
      const graphQLError = importError?.graphQLErrors?.[0]?.extensions?.details;
      const extensionDetails = Array.isArray(graphQLError) ? graphQLError : [graphQLError];

      const codeToDetailsMap = new Map<string, ErrorDetail[]>();

      for (const detail of extensionDetails) {
        const code = detail?.code;
        if (!code) continue;

        if (!codeToDetailsMap.has(code)) {
          codeToDetailsMap.set(code, []);
        }

        const existingDetails = codeToDetailsMap.get(code);
        if (existingDetails) {
          existingDetails.push(detail);
        }
      }

      setErrorMessages(codeToDetailsMap);
    },
    onCompleted: () => {
      onClose();
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      SearchExamEventExamineesDocument,
      {
        query: GetExamEventByIdDocument,
        variables: {
          getExamEventByIdInput: {
            id: examEventId,
          },
        },
      },
      {
        query: GetExamEventExamineeCountSummaryDocument,
        variables: {
          getExamEventExamineeCountSummaryInput: {
            examEventId,
          },
        },
      },
    ],
  });

  const handleClose = () => {
    onClose();
    setErrorMessages(null);
  };

  const handleFileImported = async (file: File) => {
    const { data: getUploadSignedUrlResult } = await getUploadSignedUrl({
      variables: {
        input: {
          contentType: file.type,
          examEventId,
        },
      },
    });

    if (!getUploadSignedUrlResult?.ecGetUploadSignedUrlForImportExaminees.signedUrl) {
      return Promise.reject(new Error('Failed to get signed URL'));
    }

    const {
      signedUrl,
      storageObject: { fileName },
    } = getUploadSignedUrlResult.ecGetUploadSignedUrlForImportExaminees;

    const uploadFileResponse = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!fileName) {
      return Promise.reject(new Error('Invalid file name'));
    }

    if (!uploadFileResponse.ok) {
      return Promise.reject(new Error('Failed to import examinees'));
    }

    return await importExamineesSpreadsheet({
      variables: {
        input: {
          fileName,
          examEventId,
        },
      },
    });
  };

  const handleSubmit = async (data: ImportExamineesViaFileFormInput) => {
    const { file } = data;

    setErrorMessages(null);
    setIsLoading(true);
    const result = await handleFileImported(file);
    setIsLoading(false);
    if (result.errors) {
      return Promise.reject(new Error('Failed to import examinees'));
    }
  };

  const handleOpenSnackbar = (data: ImportExamineesViaFileFormInput) => {
    showPromiseSnackbar({
      promise: () => handleSubmit(data),
      message: {
        pending: 'กำลังนำเข้ารายชื่อผู้มีสิทธิ์สอบ',
        success: 'นำเข้ารายชื่อผู้มีสิทธิ์สอบเรียบร้อยแล้ว',
        error: 'ไม่สามารถนำเข้ารายชื่อผู้มีสิทธิ์สอบได้',
      },
    });
  };

  return (
    <Dialog open={isOpen} maxWidth='sm' onClose={handleClose}>
      <DialogTitle onClose={handleClose} loading={isLoading}>
        Import Examinees
      </DialogTitle>
      <Divider />
      <ImportExamineesViaFileForm
        isLoading={isLoading}
        onClose={handleClose}
        onSubmit={handleOpenSnackbar}
        importTemplateUrl={importTemplateUrl}
        errors={errorMessages}
      />
    </Dialog>
  );
}
